import { useState, useMemo, useEffect } from 'react'
import { useAxiosApi } from '@/hooks/useAxiosApi'
import { getAugmentedVariant } from '@/lib/variant'
import { sumBy } from 'lodash'

/*
    Augmented lines are an array of objects with the following properties:
    - variant_id: string
    - quantity: number
    - price: number
    - total_price: number
    - variant: object
*/

export const useVariantLines = (initialLines = [], markupRate = 0) => {
    const [lines, setLines] = useState([])
    const [loading, setLoading] = useState(false)
    const [variants, setVariants] = useState([])

    const { requestGet } = useAxiosApi()

    // Add lines if their are initial lines
    useEffect(() => {
        if (initialLines.length > 0) {
            setLines(
                initialLines.map((line) => ({
                    id: line.id,
                    variant_id: line.shopify_product_variant.id,
                    quantity: line.total_quantity,
                    unit_price: line.unit_price_initial,
                }))
            )
        }
    }, [initialLines])

    // Get all variant ids from the lines
    const variantIds = useMemo(() => {
        return lines.map((line) => line.variant_id)
    }, [lines])

    // Fetch the variant lines from the database

    const fetchVariants = async () => {
        if (variantIds.length === 0) return

        setLoading(true)

        try {
            const { data } = await requestGet('/shopify/variants/by-ids', {
                ids: variantIds.join(','),
            })

            setVariants(data)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchVariants()
    }, [variantIds])

    // Add a variant to the lines
    const addVariant = async (variantId) => {
        // Fetch the variant from the database

        const { data } = await requestGet(`/shopify/variants/${variantId}`)

        // If the variant is already in the lines, return, add a quantity to the existing line
        const existingLine = lines.find((line) => line.variant_id === variantId)

        if (existingLine) {
            setLines(
                lines.map((line) =>
                    line.variant_id === variantId
                        ? { ...line, quantity: line.quantity + 1 }
                        : line
                )
            )
        } else {
            // Get price
            const price = data.price

            setLines([
                ...lines,
                {
                    variant_id: variantId,
                    quantity: 1,
                    unit_price: price,
                },
            ])
        }
    }

    // Update the price of a line based on the line index
    const updateLinePrice = (lineIndex, price) => {
        setLines(
            lines.map((line, index) =>
                index === lineIndex ? { ...line, unit_price: price } : line
            )
        )
    }

    // Remove a line from the lines
    const removeLine = (lineIndex) => {
        setLines(lines.filter((line, index) => index !== lineIndex))
    }

    // Update the quantity of a line
    const updateLineQuantity = (lineIndex, quantity) => {
        setLines(
            lines.map((line, index) =>
                index === lineIndex ? { ...line, quantity } : line
            )
        )
    }

    // Augmented lines
    const augmentedLines = lines
        .map((line) => {
            const variant = variants.find((v) => v.id === line.variant_id)
            if (!variant) return null

            const augmented_variant = getAugmentedVariant(variant)

            return {
                image: variant.image_url,
                product_title: variant.shopify_product.title,
                variant_title: variant.title,
                variant_id: variant.id,
                sku: variant.sku,
                unit_price: line.unit_price,
                total_price: line.unit_price * line.quantity,
                quantity: line.quantity,
                dispatch_date: augmented_variant.dispatch_date,
                availableAtLocation: augmented_variant.availableAtLocation,
                is_selling_in_shopify: augmented_variant.is_selling_in_shopify,
                isB2BDisallowed: augmented_variant.isB2BDisallowed,
                product_id: variant.shopify_product.id,
                ...(line.id && { id: line.id }),
            }
        })
        .filter((line) => line !== null)

    // Totals
    const total_without_shipping = useMemo(() => {
        return sumBy(lines, 'total_price')
    }, [lines])

    const total_quantity = useMemo(() => {
        return sumBy(lines, 'quantity')
    }, [lines])

    return {
        lines: augmentedLines,
        loading,
        addVariant,
        updateLinePrice,
        updateLineQuantity,
        removeLine,
        total_without_shipping,
        total_quantity,
    }
}
